import React, { useCallback, useEffect, useState } from "react";
import { apiGet, apiPost, apiErrorMessage } from "../../api";

/**
 * Who this invoice will sync to in QuickBooks, shown where the invoice is written.
 *
 * "Sync to QuickBooks" refuses an invoice whose client has no customer link.
 * That used to surface as a failure at the last moment (when issuing still
 * pushed), with the fix sitting on a different screen -- which is how thirteen
 * invoices stayed unmapped until someone tried to issue them.
 *
 * Unmapped is shown as a blank, not an error: it is the normal state for a
 * client nobody has mapped, and the two ways out (pick one, or create one) are
 * right here.
 */

const BOX = {
  padding: "10px 12px",
  border: "1px solid #e5e7eb",
  borderRadius: 8,
  marginBottom: 12,
};

const SMALL_BUTTON = {
  padding: "4px 10px",
  borderRadius: 6,
  border: "1px solid #d1d5db",
  background: "white",
  cursor: "pointer",
  fontSize: 12,
};

export default function QboCustomerPanel({ clientId, clientName, onLinked }) {
  const [state, setState] = useState(null);     // the for_client payload
  const [picking, setPicking] = useState(false);
  const [customers, setCustomers] = useState(null);
  const [query, setQuery] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [notConnected, setNotConnected] = useState(false);

  const load = useCallback(async () => {
    if (!clientId) return;
    setError("");
    try {
      setState(await apiGet(`/api/quickbooks/customers/for_client/?client=${clientId}`));
    } catch (e) {
      // A disconnected QuickBooks is not this panel's problem to shout about;
      // it simply has nothing to say.
      setNotConnected(true);
    }
  }, [clientId]);

  useEffect(() => {
    setState(null);
    setPicking(false);
    setCustomers(null);
    setQuery("");
    setNotConnected(false);
    load();
  }, [clientId, load]);

  const loadCustomers = useCallback(
    async (search) => {
      setBusy(true);
      setError("");
      try {
        const data = await apiGet(
          `/api/quickbooks/customers/for_client/?client=${clientId}&suggest=1` +
            (search ? `&q=${encodeURIComponent(search)}` : "")
        );
        setCustomers(data.customers || []);
      } catch (e) {
        setError(apiErrorMessage(e, "Could not reach QuickBooks."));
      } finally {
        setBusy(false);
      }
    },
    [clientId]
  );

  const startPicking = async () => {
    setPicking(true);
    await loadCustomers("");
  };

  const link = async (customer) => {
    setBusy(true);
    setError("");
    try {
      await apiPost("/api/quickbooks/customers/confirm/", {
        links: [
          {
            client_id: Number(clientId),
            qbo_customer_id: customer.qbo_customer_id,
            qbo_display_name: customer.display_name,
          },
        ],
      });
      setPicking(false);
      await load();
      if (onLinked) onLinked(customer);
    } catch (e) {
      setError(apiErrorMessage(e, "Could not link that customer."));
    } finally {
      setBusy(false);
    }
  };

  const createInQbo = async () => {
    setBusy(true);
    setError("");
    try {
      await apiPost("/api/quickbooks/customers/create_in_qbo/", {
        client_id: Number(clientId),
      });
      await load();
      if (onLinked) onLinked(null);
    } catch (e) {
      // QuickBooks refuses a name that collides with an existing customer, and
      // says which -- that is the whole value of the message.
      setError(apiErrorMessage(e, "Could not create that customer in QuickBooks."));
    } finally {
      setBusy(false);
    }
  };

  if (!clientId || notConnected) return null;

  const linked = state && state.linked;

  return (
    <div style={{ ...BOX, background: linked ? "#f9fafb" : "#fffbeb" }}>
      <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
        <span
          style={{
            fontSize: 11,
            fontWeight: 600,
            textTransform: "uppercase",
            letterSpacing: "0.04em",
            color: "#6b7280",
          }}
        >
          QuickBooks customer
        </span>

        {linked ? (
          <>
            <strong style={{ fontSize: 13 }}>{linked.qbo_display_name}</strong>
            <button type="button" onClick={startPicking} style={SMALL_BUTTON}>
              Change
            </button>
          </>
        ) : state ? (
          <>
            <span data-testid="qbo-unmapped" style={{ fontSize: 13, color: "#b45309" }}>
              Not linked — this invoice cannot be finalised until it is.
            </span>
            <button type="button" onClick={startPicking} style={SMALL_BUTTON} disabled={busy}>
              Pick a customer
            </button>
            <button type="button" onClick={createInQbo} style={SMALL_BUTTON} disabled={busy}>
              Create {clientName || "this client"} in QuickBooks
            </button>
          </>
        ) : (
          <span style={{ fontSize: 13, color: "#9ca3af" }}>Checking…</span>
        )}
      </div>

      {error && (
        <div style={{ marginTop: 8, fontSize: 12.5, color: "#b91c1c" }}>{error}</div>
      )}

      {picking && (
        <div style={{ marginTop: 10 }}>
          <input
            type="text"
            value={query}
            placeholder="Search QuickBooks customers…"
            aria-label="Search QuickBooks customers"
            onChange={(e) => {
              setQuery(e.target.value);
              loadCustomers(e.target.value);
            }}
            style={{
              width: "100%",
              padding: "6px 8px",
              border: "1px solid #d1d5db",
              borderRadius: 6,
              fontSize: 13,
              marginBottom: 8,
            }}
          />
          <div style={{ maxHeight: 220, overflowY: "auto" }}>
            {busy && <div style={{ fontSize: 12, color: "#6b7280" }}>Loading…</div>}
            {!busy && customers && customers.length === 0 && (
              <div style={{ fontSize: 12, color: "#6b7280" }}>
                No QuickBooks customer matches that.
              </div>
            )}
            {(customers || []).map((customer) => {
              const taken = Boolean(customer.linked_to_client_name);
              return (
                <div
                  key={customer.qbo_customer_id}
                  data-testid={`qbo-customer-${customer.qbo_customer_id}`}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: 10,
                    padding: "5px 0",
                    borderBottom: "1px solid #f3f4f6",
                    fontSize: 13,
                  }}
                >
                  <span>
                    {customer.display_name}
                    {taken && (
                      // Shown, not hidden: the link column is unique, so this
                      // one cannot be used -- but someone looking at it in
                      // QuickBooks needs to know why it is not on offer.
                      <span style={{ color: "#9ca3af", marginLeft: 8, fontSize: 12 }}>
                        already linked to {customer.linked_to_client_name}
                      </span>
                    )}
                  </span>
                  <button
                    type="button"
                    disabled={taken || busy}
                    onClick={() => link(customer)}
                    style={{
                      ...SMALL_BUTTON,
                      cursor: taken ? "not-allowed" : "pointer",
                      opacity: taken ? 0.5 : 1,
                    }}
                  >
                    Use this
                  </button>
                </div>
              );
            })}
          </div>
          <button
            type="button"
            onClick={() => setPicking(false)}
            style={{ ...SMALL_BUTTON, marginTop: 8 }}
          >
            Cancel
          </button>
        </div>
      )}
    </div>
  );
}
